import {useEffect, useState} from 'react';
import {useHistory} from 'react-router-dom';
import {
  Button,
  Input,
  message,
  Modal,
  Row,
  Select,
  Spin,
  Tabs,
  Typography,
  Upload,
  Checkbox,
} from 'antd';

import {CreateAssetBlock} from '../../components';
import {
  Asset,
  AssetType,
  ProgramType,
  QUESTION_TYPE,
  TestTypes,
} from '../../graphql/API';
import type {QuestionWithNewFields} from '../Questions/QuestionTypes';
import {TestsService} from './TestsService';
import {validateCreateTest} from '../../utils/Validators';
import {BackendQuestionType} from '../../types/newQuestionType';
import {CloseCircleFilled, UploadOutlined} from '@ant-design/icons';
import {UploadFile, UploadProps} from 'antd/lib/upload/interface';
import {StorageService, UploadFileType} from '../Storage/StorageService';
import {CreateAssetType} from '../../components/CreateAssetType';
import {CreateAssetUpload} from '../../components/CreateAssetUpload';
import {QUESTION_TYPE_DICT} from './QuestionTypeDict';
import {ProgramsNewService} from '../Program/ProgramsNewService';
import {
  duplicateChoicesKeywordsByWordCount,
  replaceChoicesInText,
} from './utils/comprehensionParser';
import {getQuestionStaticValues} from '../../utils/staticValuesUtils/questionStaticValues';
import {getDailyExerciseSummary} from '../../utils/staticValuesUtils/dailyExerciseStaticValue';
const testService = new TestsService();
const storageService = new StorageService();
const programsNewService = new ProgramsNewService();

export const TestCreate = () => {
  const history = useHistory();
  const {Title, Text} = Typography;
  const {TabPane} = Tabs;

  // Get initial static values for default question
  const initialValues = getQuestionStaticValues(
    QUESTION_TYPE.READ_SENTENCE,
    'PHOTO',
  );

  const emptyTestPane = {
    name: '',
    //@ts-ignore
    correctAsset: {
      type: AssetType.AUDIO,
      thumbnailUri: '',
      fileUri: '',
    },
    //@ts-ignore
    asset: {
      type: AssetType.PHOTO,
      thumbnailUri: '',
      fileUri: '',
    },
    programType: ProgramType.LANGUAGE,
    summary: initialValues.summary,
    description: initialValues.description,
    text: '',
    textAI: '',
    originalTextAI: '',
    correctIndex: {},
    choices: [],
    choicesCount: {},
    type: QUESTION_TYPE.READ_SENTENCE,
    program: '', // NEW: Program ID for the question
    difficulty: 'NORMAL', // NEW: Difficulty level (EASY, NORMAL, HARD)
  };

  //@ts-ignore
  const [panes, setPanes] = useState<Array<any>>([emptyTestPane]);
  const [activeKey, setActiveKey] = useState<any>(0);

  const [testName, setTestName] = useState<string>('');
  const [programsList, setProgramsList] = useState<any[]>([]); // NEW: Programs list

  const [fileAudio, setFileAudio] = useState<UploadFile>();

  const [uploading, setUploading] = useState(false);
  const [fileSummaryVideo, setFileSummaryVideo] = useState<UploadFile>();

  const [selectChoice, setSelectChoice] = useState<string>('');

  const [testType, setTestType] = useState<TestTypes | undefined>(
    TestTypes.QUIZ,
  );
  // @ts-ignore
  const [testAsset, setTestAsset] = useState<Asset | undefined>({
    type: AssetType.VIDEO,
    thumbnailUri: '',
    fileUri: '',
  });
  const [testDescription, setTestDescription] = useState<string>(
    'Watch the video at least 2 times and after that Record the answer to the questions.',
  );
  const [testSummary, setTestSummary] = useState<string>(
    'Watch carefully at least 2 times and answer the questions.',
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [questionName, setQuestionName] = useState<string>('');
  const [questionDescription, setQuestionDescription] = useState<string>('');
  const [questionSummary, setQuestionSummary] = useState<string>('');
  const [shownChoicesAlerts, setShownChoicesAlerts] = useState<Set<number>>(
    new Set(),
  );
  useEffect(() => {
    // NEW: Fetch programs list
    programsNewService
      .getNewPrograms()
      .then(programs => {
        console.log('programs_list', programs);
        setProgramsList(programs);

        // Auto-select first program for all panes if they don't have one
        if (programs.length > 0) {
          const firstProgramId = programs[0]._id || programs[0].id;
          setPanes(prevPanes =>
            prevPanes.map(pane => ({
              ...pane,
              program: pane.program || firstProgramId,
            })),
          );
          console.log('Auto-selected first program:', firstProgramId);
        }
      })
      .catch(error => {
        console.error('Error fetching programs:', error);
      });

    return () => {};
  }, []);

  const onChange = (activeKey: string) => {
    setActiveKey(parseInt(activeKey));
    setFileSummaryVideo(null);
  };

  const onEdit = (targetKey: any, action: any) => {
    switch (action) {
      case 'add':
        add();
        break;
      default:
    }
  };

  const add = () => {
    const activeKey = panes.length;
    const newPanes = panes;

    // Create a fresh copy of emptyTestPane to avoid reference issues
    const freshPane = {
      ...emptyTestPane,
      asset: {...emptyTestPane.asset},
      correctAsset: {...emptyTestPane.correctAsset},
    };

    // @ts-ignore
    newPanes.push(freshPane);
    setPanes(newPanes);
    setActiveKey(activeKey);
  };
  const propsAudioUpload: UploadProps = {
    type: 'select',
    accept: '.aac',
    listType: 'text',
    multiple: false,
    maxCount: 1,
    onRemove: file => {
      setFileAudio(null);
    },
    beforeUpload: file => {
      setFileAudio(file);

      return false;
    },
  };

  const propsSummaryVideo: UploadProps = {
    type: 'select',
    accept: '.mp4,.aac',
    listType: 'text',
    multiple: false,
    maxCount: 1,
    onRemove: file => {
      setFileSummaryVideo(null);
    },
    beforeUpload: file => {
      setFileSummaryVideo(file);

      return false;
    },
  };

  const handleUpload = async (
    fileUri: UploadFile,
    typeUpload: UploadFileType,
    index: number,
  ) => {
    setUploading(true);
    try {
      let filePath: string;
      switch (typeUpload) {
        case UploadFileType.TEST_MEDIA:
          filePath = await storageService.uploadFile(
            fileUri,
            typeUpload,
            'test/media',
          );
          let tempAsset = {
            fileUri: filePath,
            type: AssetType.AUDIO,
            thumbnailUri: '',
          };
          let tempPanes = [...panes];
          // @ts-ignore
          tempPanes[index].correctAsset = tempAsset;
          setPanes(tempPanes);
          break;

        case UploadFileType.TEST_MEDIA_VIDEO_SESSION:
          filePath = await storageService.uploadFile(
            fileUri,
            typeUpload,
            'test/media',
          );
          let tempPaness = [...panes];
          // @ts-ignore
          tempPaness[index].videoAISessionUri = filePath;
          setPanes(tempPaness);
          break;

        default:
          break;
      }

      //console.log('file_PATH_IUPLOADED', filePath);
      message.success('upload successfully.');

      setUploading(false);
    } catch (error) {
      setUploading(false);
      message.error('upload failed.');
    }
  };

  const handleTestAssetTypeChange = asset => {
    if (asset.type === 'PHOTO') {
      setTestDescription('Watch carefully and record with your own voice.');
      setTestSummary('Record with your own voice');
      setTestAsset(asset);
    } else if (asset.type === 'VIDEO') {
      setTestDescription(
        'Watch the video at least 2 times and after that record the answer to the questions.',
      );
      setTestSummary(
        'Watch carefully at least 2 times and answer the questions.',
      );
      setTestAsset(asset);
    } else if (asset.type === 'AUDIO') {
      setTestDescription('');
      setTestSummary('');
      setTestAsset(asset);
    }
    // //console.log("TestAsset",testAsset,asset.type);
  };
  const handleQuestionTypeChange = (
    type: string,
    asset: string,
    index?: number,
  ) => {
    const {summary, description} = getQuestionStaticValues(type, asset);

    setQuestionSummary(summary);
    setQuestionDescription(description);

    // Update the panes array if index is provided
    if (index !== undefined) {
      let tempPanes = [...panes];
      tempPanes[index].summary = summary;
      tempPanes[index].description = description;
      setPanes(tempPanes);
    }
  };

  function cleanStringForAI(input) {
    return input
      .toLowerCase() // Convert to lowercase
      .replace(/[^a-z0-9'\s]/g, ''); // Remove special characters and punctuation, except apostrophe
  }

  return (
    <>
      <div style={{padding: 10}}>
        <Title style={{textAlign: 'center'}}>Create test </Title>
        <>
          <div style={{marginTop: '20px', marginBottom: '20px'}}>
            <Text>Test name </Text>
            <Input
              onChange={e => {
                setTestName(e.target.value);
                setQuestionName(e.target.value);
                let newPanes = [...panes];
                newPanes[0].name = e.target.value;
                setPanes(newPanes);
              }}
              placeholder="Test name"
            />
          </div>
          {/* <div style={{marginTop: '20px', marginBottom: '20px'}}>
            <Text style={{display: 'block'}}>Test type </Text>
            <Select
              value={testType}
              showSearch
              style={{width: '100%'}}
              placeholder="Select a test type"
              optionFilterProp="children"
              onChange={(selectedTestType: TestTypes) => {
                setTestType(selectedTestType);
              }}>
              <Select.Option value={TestTypes.QUIZ}>
                {TestTypes.QUIZ}
              </Select.Option>
              <Select.Option value={TestTypes.PLACEMENT_TEST}>
                {TestTypes.PLACEMENT_TEST}
              </Select.Option>
              <Select.Option value={TestTypes.STORY}>
                {TestTypes.STORY}
              </Select.Option>
            </Select>
          </div> */}
          <CreateAssetType
            defaultAssetType={AssetType.VIDEO}
            // @ts-ignore TODO be checked
            onChange={selectedAsset => handleTestAssetTypeChange(selectedAsset)}
          />
          <div style={{marginTop: '20px', marginBottom: '20px'}}>
            <Text>Test description </Text>
            <Input
              value={testDescription}
              onChange={e => {
                setTestDescription(e.target.value);
              }}
              placeholder="Test description"
            />
          </div>
          <div style={{marginTop: '20px', marginBottom: '20px'}}>
            <Text>Test summary </Text>
            <Input
              value={getDailyExerciseSummary(testAsset.type, panes.length)}
              onChange={e => {
                setTestSummary(e.target.value);
              }}
              disabled
              placeholder="Test summary"
            />
          </div>

          <CreateAssetUpload
            defaultAssetType={testAsset.type}
            asset={testAsset}
            // @ts-ignore TODO be checked
            onChange={selectedAsset => setTestAsset(selectedAsset)}
          />
        </>

        <Tabs
          type="editable-card"
          onChange={onChange}
          activeKey={activeKey.toString()}
          onEdit={onEdit}>
          {panes?.map((pane, index) => (
            <TabPane
              tab={'question_' + index}
              key={index}
              closable={false}
              style={{
                border: '2px solid gray',
                padding: '10px',
              }}>
              <div style={{marginTop: '20px', marginBottom: '20px'}}>
                <Text>Question name</Text>
                <Input
                  onChange={e => {
                    let tempPanes = [...panes];
                    tempPanes[index].name = e.target.value;
                    setPanes(tempPanes);
                  }}
                  placeholder="Question name"
                  value={panes[index].name}
                />
              </div>
              <div style={{marginTop: '20px', marginBottom: '20px'}}>
                <Text style={{display: 'block'}}>Question type </Text>
                <Select
                  value={panes[index].type}
                  showSearch
                  style={{width: 300}}
                  placeholder="Select a question type"
                  optionFilterProp="children"
                  onChange={(selectedQuestionType: any) => {
                    let tempPanes = [...panes];
                    tempPanes[index].type = selectedQuestionType;
                    //@ts-ignore
                    tempPanes[index].customChoices = [];
                    setPanes(tempPanes);
                    handleQuestionTypeChange(
                      selectedQuestionType,
                      panes[index].asset.type,
                      index,
                    );
                  }}>
                  <Select.Option value={QUESTION_TYPE.READ_SENTENCE}>
                    {QUESTION_TYPE.READ_SENTENCE} (default)
                  </Select.Option>
                  {/*MUST ENTER 4 Choices*/}
                  <Select.Option value={QUESTION_TYPE.CHOICES}>
                    {QUESTION_TYPE.CHOICES}
                  </Select.Option>
                  {/*MUST ENTER TEXTAI*/}
                  <Select.Option value={QUESTION_TYPE.MISSING_LETTERS}>
                    {QUESTION_TYPE.MISSING_LETTERS}
                  </Select.Option>
                  <Select.Option value={QUESTION_TYPE.MISSING_WORD}>
                    {QUESTION_TYPE.MISSING_WORD}
                  </Select.Option>
                  {/*MUST ENTER 4 Choices*/}
                  <Select.Option value={QUESTION_TYPE.COMPREHENSION}>
                    {QUESTION_TYPE.COMPREHENSION}
                  </Select.Option>
                  {/* TRANSCRIBE_AND_SCORE */}
                  <Select.Option value={QUESTION_TYPE.TRANSCRIBE_AND_SCORE}>
                    {QUESTION_TYPE.TRANSCRIBE_AND_SCORE}
                  </Select.Option>
                  {/* DESCRIBE_IMAGE */}
                  <Select.Option value={BackendQuestionType.DESCRIBE_IMAGE}>
                    {BackendQuestionType.DESCRIBE_IMAGE}
                  </Select.Option>
                  {/* FREE_TEXT_ONLY_PRONUNCIATION */}
                  <Select.Option
                    value={QUESTION_TYPE.FREE_TEXT_ONLY_PRONUNCIATION}>
                    {QUESTION_TYPE.FREE_TEXT_ONLY_PRONUNCIATION}
                  </Select.Option>
                </Select>
              </div>
              {/* NEW: Program Dropdown */}
              <div style={{marginTop: '20px', marginBottom: '20px'}}>
                <Text>Program *</Text>
                <Select
                  style={{width: '100%', marginTop: '5px'}}
                  placeholder="Select a program"
                  value={panes[index].program || undefined}
                  optionFilterProp="children"
                  onChange={(selectedProgramId: string) => {
                    let tempPanes = [...panes];
                    tempPanes[index].program = selectedProgramId;
                    setPanes(tempPanes);
                  }}>
                  {programsList.map((program: any, programIndex) => (
                    <Select.Option
                      value={program.id || program._id}
                      key={programIndex}>
                      {program.name}
                    </Select.Option>
                  ))}
                </Select>
              </div>
              {/* NEW: Difficulty Selector */}
              <div style={{marginTop: '20px', marginBottom: '20px'}}>
                <Text>Difficulty Level</Text>
                <Select
                  style={{width: '100%', marginTop: '5px'}}
                  placeholder="Select difficulty"
                  value={panes[index].difficulty || 'NORMAL'}
                  optionFilterProp="children"
                  onChange={(
                    selectedDifficulty: 'EASY' | 'NORMAL' | 'HARD',
                  ) => {
                    let tempPanes = [...panes];
                    tempPanes[index].difficulty = selectedDifficulty;
                    setPanes(tempPanes);
                  }}>
                  <Select.Option value="EASY">Easy</Select.Option>
                  <Select.Option value="NORMAL">Normal</Select.Option>
                  <Select.Option value="HARD">Hard</Select.Option>
                </Select>
              </div>
              <div style={{marginTop: '20px', marginBottom: '20px'}}>
                <Text>Question Summary</Text>
                <Input
                  value={(() => {
                    const result = getQuestionStaticValues(
                      panes[index].type,
                      testAsset?.type,
                    );
                    return result.summary;
                  })()}
                  onChange={e => {}}
                  placeholder="Question Summary"
                  disabled
                />
              </div>
              <div style={{marginTop: '20px', marginBottom: '20px'}}>
                <Text>Question description</Text>
                <Input
                  value={(() => {
                    const result = getQuestionStaticValues(
                      panes[index].type,
                      testAsset?.type,
                    );
                    return result.description;
                  })()}
                  onChange={e => {}}
                  placeholder="Question description"
                  disabled
                />
              </div>

              {testType === TestTypes.STORY && (
                <div style={{marginTop: '20px', marginBottom: '20px'}}>
                  <Text>Video AI Session url </Text>

                  {panes[index].videoAISessionUri ? (
                    <Row
                      style={{
                        display: 'flex',
                        // justifyContent: 'center',
                        alignItems: 'center',
                        alignContent: 'center',
                      }}>
                      <Input
                        style={{
                          width: '80%',
                        }}
                        value={panes[index].videoAISessionUri}
                        onChange={e => {}}
                        placeholder="Basic usage"
                      />
                      {panes[index].videoAISessionUri && (
                        <CloseCircleFilled
                          style={{marginInline: 20}}
                          onClick={() => {
                            let tempPanes = [...panes];
                            tempPanes[index].videoAISessionUri = null;
                            setPanes(tempPanes);
                            setFileSummaryVideo(null);
                          }}
                        />
                      )}
                    </Row>
                  ) : (
                    <Row>
                      <Upload {...propsSummaryVideo}>
                        <Button
                          disabled={fileSummaryVideo ? true : false}
                          icon={<UploadOutlined />}>
                          Select File
                        </Button>
                      </Upload>

                      <Button
                        type="primary"
                        onClick={() => {
                          handleUpload(
                            fileSummaryVideo,
                            UploadFileType.TEST_MEDIA_VIDEO_SESSION,
                            index,
                          );
                        }}
                        disabled={!fileSummaryVideo}
                        loading={uploading}>
                        {uploading ? 'Uploading' : 'Start Upload'}
                      </Button>
                    </Row>
                  )}
                </div>
              )}

              {pane.type === QUESTION_TYPE.CHOICES ? (
                <div>
                  {new Array(4).fill(null).map((_, arrayIndex) => {
                    return (
                      <div
                        key={arrayIndex}
                        style={{marginTop: '20px', marginBottom: '20px'}}>
                        <Text>Choice number {arrayIndex + 1}</Text>
                        <Input
                          value={panes[index].choices[arrayIndex]}
                          onChange={e => {
                            let tempPanes = [...panes];
                            //@ts-ignore
                            tempPanes[index].choices[arrayIndex] =
                              e.target.value;

                            setPanes(tempPanes);
                          }}
                          placeholder={`Choice number ${arrayIndex + 1}`}
                        />
                      </div>
                    );
                  })}
                  <div
                    style={{
                      marginTop: '20px',
                      marginBottom: '20px',
                      border: '2px solid red',
                    }}>
                    <Text>Question text</Text>
                    <Input
                      value={panes[index].text}
                      onChange={e => {
                        let tempPanes = [...panes];
                        tempPanes[index].text = e.target.value;
                        tempPanes[index].textAI = cleanStringForAI(
                          e.target.value,
                        );
                        setPanes(tempPanes);
                      }}
                      placeholder="Question text"
                    />
                  </div>
                </div>
              ) : (
                <div style={{marginTop: '20px', marginBottom: '20px'}}>
                  <Text>Question text</Text>
                  <Input
                    value={panes[index].text}
                    onBlur={() => {
                      if (pane.type === QUESTION_TYPE.COMPREHENSION) {
                        // Check BOTH text and textAI fields for CHOICES keywords
                        const textChoices = (panes[index].text || '').split(
                          ' ',
                        );
                        const aiTextChoices = (panes[index].textAI || '').split(
                          ' ',
                        );
                        const allChoices = [...textChoices, ...aiTextChoices];

                        let newOptions = {};
                        let newChoicesCount = {};

                        // Find all CHOICES keywords from both fields
                        const numOfchoiceOptions = allChoices
                          .filter(text => text.startsWith('CHOICES'))
                          .map(text => text.replace(/[.,!?:;]+$/, '')); // Strip trailing punctuation

                        // Remove duplicates using Set
                        const uniqueChoiceOptions = [
                          ...new Set(numOfchoiceOptions),
                        ];

                        // Show alert only once if no CHOICES keywords found
                        if (uniqueChoiceOptions.length === 0) {
                          if (!shownChoicesAlerts.has(index)) {
                            alert(
                              'Must Text or AI Text Contains at least one keyword CHOICES1',
                            );
                            setShownChoicesAlerts(
                              new Set(shownChoicesAlerts).add(index),
                            );
                          }
                        } else {
                          // Remove from shown alerts if user has fixed the issue
                          if (shownChoicesAlerts.has(index)) {
                            const newAlerts = new Set(shownChoicesAlerts);
                            newAlerts.delete(index);
                            setShownChoicesAlerts(newAlerts);
                          }
                        }

                        uniqueChoiceOptions.forEach(value => {
                          newOptions[value] = [];
                          newChoicesCount[value] = 2; // Initialize with 2 choices
                        });

                        let tempPanes = [...panes];
                        //@ts-ignore
                        tempPanes[index].customChoices = newOptions;
                        //@ts-ignore
                        tempPanes[index].choicesCount = newChoicesCount;

                        // Auto-select first CHOICES keyword
                        if (uniqueChoiceOptions.length > 0) {
                          setSelectChoice(uniqueChoiceOptions[0]);
                        }

                        setPanes(tempPanes);
                      } else {
                        let tempPanes = [...panes];

                        //@ts-ignore
                        tempPanes[index].customChoices = {};
                        setPanes(tempPanes);
                      }
                    }}
                    onChange={e => {
                      let tempPanes = [...panes];
                      tempPanes[index].text = e.target.value;

                      if (
                        tempPanes[index].type === QUESTION_TYPE.COMPREHENSION
                      ) {
                        tempPanes[index].textAI = e.target.value;
                        // store the original ai text to able the user change the correct answer
                        //@ts-ignore
                        tempPanes[index].originalTextAI = e.target.value;
                      } else {
                        tempPanes[index].textAI = cleanStringForAI(
                          e.target.value,
                        );
                      }

                      setPanes(tempPanes);
                    }}
                    placeholder="Question text"
                  />
                </div>
              )}
              {QUESTION_TYPE_DICT[pane.type] == 'NORMAL_SCORE' && (
                <div
                  style={{
                    marginTop: '20px',
                    marginBottom: '20px',
                    border: '2px solid red',
                  }}>
                  <Text>Question AI text</Text>
                  <Input
                    value={panes[index].textAI}
                    onBlur={() => {
                      if (pane.type === QUESTION_TYPE.COMPREHENSION) {
                        // Check BOTH text and textAI fields for CHOICES keywords
                        const textChoices = (panes[index].text || '').split(
                          ' ',
                        );
                        const aiTextChoices = (panes[index].textAI || '').split(
                          ' ',
                        );
                        const allChoices = [...textChoices, ...aiTextChoices];

                        let newOptions = {};
                        let newChoicesCount = {};

                        // Find all CHOICES keywords from both fields
                        const numOfchoiceOptions = allChoices
                          .filter(text => text.startsWith('CHOICES'))
                          .map(text => text.replace(/[.,!?:;]+$/, '')); // Strip trailing punctuation

                        // Remove duplicates using Set
                        const uniqueChoiceOptions = [
                          ...new Set(numOfchoiceOptions),
                        ];

                        // Show alert only once if no CHOICES keywords found
                        if (uniqueChoiceOptions.length === 0) {
                          if (!shownChoicesAlerts.has(index)) {
                            alert(
                              'Must Text or AI Text Contains at least one keyword CHOICES1',
                            );
                            setShownChoicesAlerts(
                              new Set(shownChoicesAlerts).add(index),
                            );
                          }
                        } else {
                          // Remove from shown alerts if user has fixed the issue
                          if (shownChoicesAlerts.has(index)) {
                            const newAlerts = new Set(shownChoicesAlerts);
                            newAlerts.delete(index);
                            setShownChoicesAlerts(newAlerts);
                          }
                        }

                        uniqueChoiceOptions.forEach(value => {
                          newOptions[value] = [];
                          newChoicesCount[value] = 2; // Initialize with 2 choices
                        });

                        let tempPanes = [...panes];
                        //@ts-ignore
                        tempPanes[index].customChoices = newOptions;
                        //@ts-ignore
                        tempPanes[index].choicesCount = newChoicesCount;

                        // Auto-select first CHOICES keyword
                        if (uniqueChoiceOptions.length > 0) {
                          setSelectChoice(uniqueChoiceOptions[0]);
                        }

                        setPanes(tempPanes);
                      } else {
                        let tempPanes = [...panes];

                        //@ts-ignore
                        tempPanes[index].customChoices = {};
                        setPanes(tempPanes);
                      }
                    }}
                    onChange={e => {
                      let tempPanes = [...panes];

                      if (
                        tempPanes[index].type === QUESTION_TYPE.COMPREHENSION
                      ) {
                        tempPanes[index].textAI = e.target.value;
                      } else {
                        tempPanes[index].textAI = cleanStringForAI(
                          e.target.value,
                        );
                      }

                      setPanes(tempPanes);
                    }}
                    placeholder="Question AI text"
                  />
                </div>
              )}

              {pane.type === QUESTION_TYPE.COMPREHENSION && (
                <div>
                  <div>
                    <Text style={{display: 'block'}}>Choice Options </Text>

                    <Select
                      value={selectChoice}
                      showSearch
                      style={{width: '30%'}}
                      placeholder={'Select ' + selectChoice}
                      optionFilterProp="children"
                      onChange={(selectedChoice: string) => {
                        setSelectChoice(selectedChoice);
                        let tempPanes = [...panes];
                        setPanes(tempPanes);
                      }}>
                      {Object.keys(panes[index].customChoices).map(choice => (
                        <Select.Option key={choice} value={choice}>
                          {choice}
                        </Select.Option>
                      ))}
                    </Select>
                  </div>
                  {selectChoice && (
                    <div>
                      {/* set 2 choices as an initial choices  */}
                      {/* @ts-ignore */}
                      {new Array(
                        //@ts-ignore
                        panes[index]?.choicesCount?.[selectChoice] || 2,
                      )
                        .fill(null)
                        .map((_, arrayIndex) => {
                          const pane = panes[index];
                          const customChoices =
                            pane?.customChoices?.[selectChoice] || [];
                          return (
                            <div
                              key={arrayIndex}
                              style={{marginTop: '20px', marginBottom: '20px'}}>
                              <Row align="middle">
                                <Text>
                                  Choice number {arrayIndex + 1}
                                  <Checkbox
                                    style={{marginLeft: '10px'}}
                                    checked={
                                      //@ts-ignore
                                      pane?.correctIndex?.[selectChoice] ===
                                      arrayIndex
                                    }
                                    onChange={() => {
                                      if (!pane) return;
                                      let tempPanes = [...panes];

                                      // Initialize correctIndex if needed
                                      //@ts-ignore
                                      if (!tempPanes[index].correctIndex) {
                                        //@ts-ignore
                                        tempPanes[index].correctIndex = {};
                                      }

                                      // Set correct answer index
                                      //@ts-ignore
                                      tempPanes[index].correctIndex[
                                        selectChoice
                                      ] = arrayIndex;

                                      // Get original text (with CHOICES keywords)
                                      //@ts-ignore
                                      const originalText =
                                        tempPanes[index].originalTextAI ||
                                        tempPanes[index].text ||
                                        '';

                                      // Build blanks array from customChoices
                                      //@ts-ignore
                                      const blanks = Object.keys(
                                        tempPanes[index].correctIndex,
                                      ).map(choiceKey => {
                                        //@ts-ignore
                                        const correctIndex =
                                          tempPanes[index].correctIndex[
                                            choiceKey
                                          ];
                                        const correctAnswer =
                                          tempPanes[index].customChoices[
                                            choiceKey
                                          ]?.[correctIndex] || choiceKey;

                                        return {
                                          name: choiceKey,
                                          choices:
                                            tempPanes[index].customChoices[
                                              choiceKey
                                            ] || [],
                                          correctAnswer: correctAnswer,
                                        };
                                      });

                                      // Update text (cleanup duplicate CHOICES keywords)
                                      tempPanes[index].text =
                                        duplicateChoicesKeywordsByWordCount(
                                          originalText,
                                          blanks,
                                        );

                                      // Update textAI (replace CHOICES keywords with correct answers)
                                      tempPanes[index].textAI =
                                        replaceChoicesInText(
                                          originalText,
                                          blanks,
                                        );

                                      setPanes(tempPanes);
                                    }}
                                  />{' '}
                                  Correct answer
                                </Text>
                              </Row>
                              <Input
                                value={customChoices[arrayIndex] || ''}
                                onChange={e => {
                                  if (!pane) return;
                                  let tempPanes = [...panes];
                                  const currentChoices =
                                    tempPanes[index].customChoices[
                                      selectChoice
                                    ] || [];
                                  currentChoices[arrayIndex] = e.target.value;

                                  tempPanes[index].customChoices[selectChoice] =
                                    currentChoices;
                                  setPanes(tempPanes);
                                }}
                                placeholder={`Choice number ${arrayIndex + 1}`}
                              />
                            </div>
                          );
                        })}
                      <Button
                        type="dashed"
                        onClick={() => {
                          let tempPanes = [...panes];

                          let currentCount =
                            //@ts-ignore
                            tempPanes[index].choicesCount[selectChoice] || 2;

                          if (currentCount < 4) {
                            //@ts-ignore
                            tempPanes[index].choicesCount[selectChoice] =
                              currentCount + 1;
                            setPanes(tempPanes);
                          }
                        }}
                        //@ts-ignore
                        disabled={panes[index].choicesCount[selectChoice] >= 4}
                        style={{marginTop: '10px'}}>
                        Add Choice
                      </Button>
                    </div>
                  )}
                </div>
              )}
              <div>
                <Text style={{fontSize: '20', fontWeight: 'bold'}}>
                  Question asset
                </Text>
                <CreateAssetBlock
                  defaultAssetType={AssetType.PHOTO}
                  key={`asset-block-${index}`}
                  // @ts-ignore TODO be checked
                  onChange={asset => {
                    // Update summary and description when asset type changes
                    const staticValues = getQuestionStaticValues(
                      panes[index].type,
                      asset.type || 'default',
                    );

                    // Create a new array with the updated pane object (deep copy of the specific pane)
                    const tempPanes = [...panes];
                    tempPanes[index] = {
                      ...panes[index],
                      asset: asset,
                      summary: staticValues.summary,
                      description: staticValues.description,
                    };

                    setPanes(tempPanes);
                  }}
                />
              </div>
              {QUESTION_TYPE_DICT[pane.type] == 'NORMAL_SCORE' && (
                <>
                  <div>
                    <Text style={{fontSize: '20', fontWeight: 'bold'}}>
                      Question correct answer asset file url
                    </Text>

                    {panes[index]?.correctAsset?.fileUri ? (
                      <Row
                        style={{
                          display: 'flex',
                          // justifyContent: 'center',
                          alignItems: 'center',
                          alignContent: 'center',
                        }}>
                        <Input
                          style={{width: '80%'}}
                          value={panes[index]?.correctAsset?.fileUri}
                          onChange={e => {}}
                          placeholder="Correct answer url"
                        />

                        <CloseCircleFilled
                          style={{marginInline: 20}}
                          onClick={() => {
                            setFileAudio(null);

                            let tempAsset = {
                              fileUri: '',
                              type: AssetType.AUDIO,
                              thumbnailUri: '',
                            };
                            let tempPanes = [...panes];
                            // @ts-ignore
                            tempPanes[index].correctAsset = tempAsset;
                            setPanes(tempPanes);
                          }}
                        />
                      </Row>
                    ) : (
                      <Row>
                        <Upload {...propsAudioUpload}>
                          <Button
                            disabled={fileAudio ? true : false}
                            icon={<UploadOutlined />}>
                            Select File
                          </Button>
                        </Upload>

                        <Button
                          type="primary"
                          onClick={() =>
                            handleUpload(
                              fileAudio,
                              UploadFileType.TEST_MEDIA,
                              index,
                            )
                          }
                          disabled={!fileAudio}
                          loading={uploading}>
                          {uploading ? 'Uploading' : 'Upload'}
                        </Button>
                      </Row>
                    )}
                  </div>
                </>
              )}
            </TabPane>
          ))}
        </Tabs>

        <Button
          onClick={async () => {
            // setIsLoading(true);
            let test = {
              questions: panes,
              type: testType,
              name: testName,
              description: testDescription,
              summary: testSummary,
              asset: testAsset,
            };

            // @ts-ignore
            let testValidationResult = validateCreateTest(test);
            if (testValidationResult?.status) {
              await testService
                .createTest(test)
                .catch(e => {
                  setIsLoading(true);
                  alert('Error creating test');
                })
                .then(response => {
                  alert('Test with ID: ' + response?.id + ' created');
                  history.push('/tests');
                });
            } else {
              alert(testValidationResult.message);
              setIsLoading(false);
            }
          }}>
          Create
        </Button>
      </div>
      <Modal
        okButtonProps={{hidden: true}}
        cancelButtonProps={{hidden: true}}
        visible={isLoading}
        closable={true}>
        <div>
          <Spin size="large" style={{margin: 'auto', width: '100%'}} />
          <p style={{textAlign: 'center'}}>Loading...</p>
        </div>
      </Modal>
    </>
  );
};
